import { Area, BackMaterialInfo, Store, Walls } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { Euler } from 'three'

function materialFromTexture(textureToAdd: Store['texture1'] | undefined) {
  if (!textureToAdd) return null
  return {
    zones: {
      area: {
        position: toVector3(),
        scale: toVector3(),
        rotation: new Euler(),
        name: 'area',
        index: 0,
        material: { ...textureToAdd },
      },
    },
    textures: [
      {
        id: textureToAdd?.id,
        searchID: textureToAdd?.searchID,
        zoneName: 'area',
        zoneDisplayName: 'MSG_182',
        url: textureToAdd?.url,
        name: textureToAdd?.name,
        zone: 'area',
        groupID: textureToAdd?.groupID,
      },
    ] as BackMaterialInfo[],
  }
}

function orderedWallIdsForArea(store: Store, areaId: string): string[] {
  const area = store.areas.get(areaId)
  const existing = (area?.sides ?? []).filter((id) => isWall(store.items.get(id)))
  if (existing.length >= 3) return existing

  const walls = Array.from(store.items.values()).filter(
    (item): item is Walls => isWall(item) && Boolean(item.areas?.has(areaId)),
  )
  if (walls.length < 3) return []

  const start = walls.find((wall) => wall.isFirst) ?? walls[0]
  const ordered: string[] = []
  const seen = new Set<string>()
  let current: Walls | undefined = start
  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    ordered.push(current.id)
    const next = current.nextWall ? store.items.get(current.nextWall) : undefined
    current = isWall(next) && next.areas?.has(areaId) ? next : undefined
  }
  return ordered.length >= 3 ? ordered : walls.map((wall) => wall.id)
}

function applyTextureToArea(area: Area, store: Store) {
  const textureToAdd = area.isRoof ? store.texture4 : store.texture1
  const payload = materialFromTexture(textureToAdd)
  if (!payload) return
  if (area.textures?.length && area.zones && 'area' in area.zones) return
  area.zones = payload.zones as Area['zones']
  area.textures = payload.textures
}

/** Cierra suelo y techo con los mismos lados. */
export function closeFloorAndRoof(store: Store, areaId?: string, roofId?: string) {
  const floorId = areaId || store.currentArea
  const roofKey = roofId || store.currentRoof
  const floor = store.areas.get(floorId)
  const roof = store.areas.get(roofKey)
  if (!floor) return
  const sides = orderedWallIdsForArea(store, floorId)
  if (sides.length >= 3) {
    floor.sides = sides
  }
  floor.isClosed = true
  if (!floor.id) floor.id = floorId
  if (roof) {
    roof.sides = [...floor.sides]
    roof.isClosed = true
    roof.isRoof = true
    if (!roof.id) roof.id = roofKey
    let height = roof.height || 0
    for (const wallId of roof.sides) {
      const wall = store.items.get(wallId)
      if (isWall(wall) && wall.height > height) height = wall.height
    }
    roof.height = height
  }
}

/**
 * Tras cargar texturas o al entrar al planner: repara áreas cerradas
 * que se crearon sin sides / sin material porque texture1/texture4 aún no existían.
 */
export function hydrateClosedRoomAreas(store: Store) {
  store.areas.forEach((area, areaId) => {
    const sides = orderedWallIdsForArea(store, areaId)
    if (sides.length >= 3) {
      area.sides = sides
      const first = store.items.get(sides[0])
      const last = store.items.get(sides[sides.length - 1])
      if (isWall(first) && isWall(last) && last.nextWall === first.id) {
        area.isClosed = true
      }
    }
    applyTextureToArea(area, store)
  })
}
